import type { MatchesOwner, MatchesOwnerLifecycle } from "./matchesOwnerLifecycle";
// (MutationTicket viaja implícito en el closure de runMatchesMutation.)

/** Wiring REAL del productor de matches (13º gate de #135), extraído de
 * AdminTournamentRoundsPage para que la regresión ejecute el flujo completo
 * — setters, checkpoints, catch/finally, keepError, unmount y reload
 * post-mutación — contra código de producción, no contra una copia. */

/** Señal interna: el load caducó (otro load/mutación/dispose lo alcanzó). */
class StaleLoadError extends Error {
  constructor() {
    super("stale-load");
    this.name = "StaleLoadError";
  }
}

export interface ProducerLoadOptions {
  /** Scope vigente de ESTE load: sesión (token exacto) + torneo + división.
   * El lifecycle lo usa para caducar boletos de mutación al cambiar de
   * scope (16º gate); vive solo en memoria. */
  scope: string;
  /** Conserva el error visible (reloads posteriores a una mutación fallida). */
  keepError?: boolean;
  setLoading(loading: boolean): void;
  clearError(): void;
  onError(err: unknown): void;
  /** El cuerpo del load: hace sus awaits y llama checkpoint() después de
   * CADA uno, antes de cualquier setter/ref — si el load caducó, checkpoint
   * corta la ejecución ahí mismo (nada posterior corre). stampOwner debe
   * llamarse ANTES de los setters de datos: estampa al dueño solo si el
   * load sigue vigente y no hay mutación abierta, y si NO puede estampar
   * ABORTA el load completo (15º gate: jamás un snapshot intermedio de
   * setters/refs con estampado rehusado). */
  run(checkpoint: () => void, stampOwner: (owner: MatchesOwner) => void): Promise<void>;
}

export async function runProducerLoad(
  lifecycle: MatchesOwnerLifecycle,
  opts: ProducerLoadOptions,
): Promise<void> {
  // Productor desmontado: cero setters, cero fetches — y beginLoad jamás
  // "reabre" el lifecycle (tras dispose sus tokens nacen caducos).
  if (lifecycle.isDisposed()) return;
  const token = lifecycle.beginLoad(opts.scope);
  opts.setLoading(true);
  if (!opts.keepError) opts.clearError();
  const checkpoint = () => {
    if (!lifecycle.isCurrent(token)) throw new StaleLoadError();
  };
  // Estampado que ABORTA: si el dueño no puede estamparse (mutación
  // abierta, load caducado o dispose), el load entero se corta como stale —
  // ningún setter/ref posterior aplica un snapshot intermedio (15º gate).
  const stampOwnerOrAbort = (owner: MatchesOwner) => {
    if (!lifecycle.stampOwner(token, owner)) throw new StaleLoadError();
  };
  try {
    await opts.run(checkpoint, stampOwnerOrAbort);
  } catch (err) {
    if (err instanceof StaleLoadError) return;
    // Un load caducado no escribe ni su error; un reload keepError conserva
    // el error original de la mutación.
    if (!lifecycle.isCurrent(token)) return;
    if (opts.keepError) return;
    opts.onError(err);
  } finally {
    // Solo el load vigente limpia su loading (un stale no toca nada; tras
    // dispose, isCurrent es false y no hay mutación de estado alguna).
    if (lifecycle.isCurrent(token)) opts.setLoading(false);
  }
}

/** Señal interna: la mutación quedó stale a media ejecución — cambió el
 * scope (división/sesión, incluso A→B→A), abrió una mutación más nueva, o
 * el productor fue desmontado. */
class StaleMutationError extends Error {
  constructor() {
    super("stale-mutation");
    this.name = "StaleMutationError";
  }
}

export interface MatchesMutationOptions {
  /** La mutación: API + setters de éxito. DEBE llamar checkpoint() después
   * de CADA await, antes de cualquier setter/ref — si la mutación quedó
   * stale (cambio de scope división/sesión — incluso A→B→A —, una mutación
   * más nueva, o unmount), checkpoint corta la ejecución ahí mismo (14º-16º
   * gates: un setter fuera de su scope es imposible). Puede pedir conservar
   * su error (caminos de negocio sin excepción) devolviendo
   * {keepErrorOnReload}. */
  mutate(checkpoint: () => void): Promise<{ keepErrorOnReload?: boolean } | void>;
  onError(err: unknown): void;
  /** El reload del productor (load de la página). Corre en el finally,
   * DESPUÉS de endMutation — solo entonces un load fresco puede estampar. */
  reload(reloadOpts: { keepError: boolean }): Promise<void>;
  /** Limpieza de UI (spinners). */
  finish?(): void;
}

/** El camino único de toda mutación del cuadro (regenerar/swap/editar):
 * begin ANTES de la API (caduca loads, cierra snapshot, bloquea estampados,
 * ata la mutación a su scope+attempt); end + reload ÚNICO en el finally
 * (éxito y fallo comparten camino; keepError conserva el error original).
 * Una mutación STALE — cambió el scope (división/sesión, incluso A→B→A),
 * abrió una mutación más nueva, o hubo unmount — no ejecuta setters,
 * onError, reload ni finish (16º gate); su endMutation SÍ corre: el
 * contador de mutaciones abiertas jamás queda filtrado. */
export async function runMatchesMutation(
  lifecycle: MatchesOwnerLifecycle,
  opts: MatchesMutationOptions,
): Promise<void> {
  const ticket = lifecycle.beginMutation();
  let keepError = false;
  const checkpoint = () => {
    if (!lifecycle.isMutationCurrent(ticket)) throw new StaleMutationError();
  };
  try {
    const result = await opts.mutate(checkpoint);
    keepError = result?.keepErrorOnReload ?? false;
  } catch (err) {
    // Mutación stale a media ejecución: aborto silencioso — el finally
    // (gateado por el mismo boleto) tampoco relanza nada.
    if (err instanceof StaleMutationError) return;
    keepError = true;
    if (lifecycle.isMutationCurrent(ticket)) opts.onError(err);
  } finally {
    lifecycle.endMutation(ticket); // SIEMPRE — contador del scope íntegro
    // keepError se AGREGA por grupo de mutaciones del mismo scope (17º
    // gate): si esta mutación pidió conservar el error pero no le toca
    // recargar (quedan otras abiertas), lo deposita — la última en cerrar
    // no lo pisa con su propio false.
    if (keepError) lifecycle.noteKeepError(ticket);
    try {
      // Reload: SOLO bajo el mismo scope (el load capturado de A jamás
      // corre bajo B) y solo cuando ya no quedan mutaciones abiertas DE ESE
      // scope — la ÚLTIMA en cerrar recarga aunque su attempt haya sido
      // superado; si nadie recargara, la página quedaría congelada.
      if (lifecycle.isMutationScopeCurrent(ticket) && !lifecycle.hasOpenMutations(ticket)) {
        await opts.reload({ keepError: lifecycle.consumeKeepError(ticket) });
      }
    } catch {
      // El reload es dueño de su propio estado (el load de la página no
      // rechaza; esto es cinturón): un reload que rechace jamás debe dejar
      // el spinner colgado (18º gate).
    } finally {
      // finish SIEMPRE que el scope siga vigente — en finally: corre aunque
      // el reload rechace (18º gate). Cada mutación del scope corre SU
      // finish al cerrar; el OWNERSHIP del spinner vive en el caller
      // (contador de operaciones / setter condicional): una vieja solo
      // libera su propia operación, jamás apaga la de una nueva. La de un
      // scope ajeno no toca la UI del vigente (16º gate).
      if (lifecycle.isMutationScopeCurrent(ticket)) opts.finish?.();
    }
  }
}
